import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Database, Server, Link, Cpu, ArrowRight } from 'lucide-react';

const Tutorial = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return null;
  }
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">Getting Started with Your AI Assistant</h1>
        <p className="text-lg text-gray-600">
          Learn how to connect your data sources and interact with them using natural language
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            This AI Assistant allows you to interact with your data sources using natural language. 
            You can connect APIs and databases, organize them into collections, and query them 
            through a conversational interface.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="text-center p-4 border rounded-lg">
              <Database className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <h3 className="font-semibold">Collections</h3>
              <p className="text-sm text-gray-600">Organize your data sources</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <Server className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <h3 className="font-semibold">Data Sources</h3>
              <p className="text-sm text-gray-600">APIs and databases</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <Cpu className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <h3 className="font-semibold">Agents</h3>
              <p className="text-sm text-gray-600">Secure database connectors</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Understanding Collections
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            <strong>Collections</strong> are containers that organize your data sources. Think of them as 
            folders that group related APIs and databases together.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Why use Collections?</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Group related data sources for easier management</li>
              <li>Control which data sources the AI can access during conversations</li>
              <li>Organize data sources by project, environment, or purpose</li>
            </ul>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Creating a Collection</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Click "New Collection" in the sidebar</li>
              <li>Give your collection a descriptive name</li>
              <li>Add data sources to your collection</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="w-5 h-5" />
            Data Sources: APIs and Databases
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Data sources are the APIs and databases you want to interact with through natural language.
          </p>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold flex items-center gap-2 mb-2">
                <Link className="w-4 h-4" />
                REST APIs
              </h4>
              <p className="text-sm mb-2">
                Connect to any REST API by providing:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm ml-4">
                <li>Base URL (e.g., https://api.example.com)</li>
                <li>Authentication details (if required)</li>
                <li>OpenAPI/Swagger documentation (optional but recommended)</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold flex items-center gap-2 mb-2">
                <Database className="w-4 h-4" />
                SQL Databases
              </h4>
              <p className="text-sm mb-2">
                Connect to PostgreSQL or MySQL databases securely:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm ml-4">
                <li>Requires setting up a database agent</li>
                <li>Uses read-only credentials for safety</li>
                <li>Automatically discovers your database schema</li>
              </ul>
            </div>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg">
            <p className="text-sm">
              <strong>Note:</strong> The AI uses function calling to determine which API to call based on your 
              natural language query. For local or VPN-protected APIs, the frontend executes the calls directly.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="w-5 h-5" />
            Database Agents
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            <strong>Agents</strong> are secure connectors that allow the AI Assistant to query your databases. 
            They run as Docker containers in your infrastructure and communicate securely with the cloud backend.
          </p>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Key Features</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Uses read-only database credentials for security</li>
              <li>Automatically discovers and publishes your database schema</li>
              <li>Validates all queries to prevent SQL injection</li>
              <li>Runs in your infrastructure - your data never leaves your network</li>
            </ul>
          </div>

          <div className="space-y-4 mt-4">
            <h4 className="font-semibold">Setting up a Database Agent</h4>
            
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                <div>
                  <h5 className="font-semibold">Create an Agent</h5>
                  <p className="text-sm text-gray-600">Go to Agent Management and click "Create New Agent"</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                <div>
                  <h5 className="font-semibold">Deploy the Agent</h5>
                  <p className="text-sm text-gray-600">Choose local deployment (Docker) or cloud deployment (ECS)</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">3</div>
                <div>
                  <h5 className="font-semibold">Configure Database Connection</h5>
                  <p className="text-sm text-gray-600">Provide your database credentials and connection details</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">4</div>
                <div>
                  <h5 className="font-semibold">Add to Collection</h5>
                  <p className="text-sm text-gray-600">Once connected, add the database to your collection</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg mt-4">
            <p className="text-sm">
              <strong>Tip:</strong> For local development, use the Docker deployment option. For production, 
              consider deploying to AWS ECS for better scalability and management.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRight className="w-5 h-5" />
            Next Steps
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>Now that you understand the basics, here's how to get started:</p>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong>Create your first collection</strong> - Give it a meaningful name like "Production APIs" or "Development Environment"
            </li>
            <li>
              <strong>Add a data source</strong> - Start with a simple REST API or set up a database agent
            </li>
            <li>
              <strong>Start chatting</strong> - Ask questions in natural language like "Show me all users created today" or "What's the current system status?"
            </li>
          </ol>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm">
              <strong>Example queries you can try:</strong>
            </p>
            <ul className="list-disc list-inside text-sm mt-2 space-y-1">
              <li>"Get all orders from the last week"</li>
              <li>"Show me the top 10 customers by revenue"</li>
              <li>"What's the current API status?"</li>
              <li>"Find all products with low inventory"</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Tutorial;